import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Handshake, Search, Eye, CheckCircle2, XCircle, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_CLS = {
  PENDING_OTP: "text-slate-400 border-slate-600 bg-slate-600/10",
  PENDING_ADMIN_APPROVAL: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  ACTIVE: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  REJECTED: "text-red-400 border-red-500/40 bg-red-500/10",
  SUSPENDED: "text-orange-400 border-orange-500/40 bg-orange-500/10",
};

export default function AdminPartners() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState(null);
  const [filters, setFilters] = useState({ status: "", city: "", country: "", q: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [audit, setAudit] = useState([]);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectMode, setRejectMode] = useState("reject");
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/partners/stats").then((r) => setStats(r.data)).catch(() => navigate("/admin/login"));
    api.get("/admin/partners", { params }).then((r) => setPartners(r.data)).catch(() => {});
  }, [navigate, filters]);
  useEffect(() => { load(); }, [load]);

  const openView = async (p) => {
    setSelected(p);
    const { data } = await api.get(`/admin/partners/${p.partner_id}/audit`);
    setAudit(data);
  };

  const approve = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/admin/partners/${confirmApprove.partner_id}/approve`);
      toast.success(`Partner approved — account ACTIVE. Referral: ${data.referral_link}`);
      setConfirmApprove(null);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const submitReason = async () => {
    setWorking(true);
    try {
      await api.post(`/admin/partners/${rejectTarget.partner_id}/${rejectMode}`, { reason });
      toast.success(rejectMode === "reject" ? "Application rejected" : "Account suspended");
      setRejectTarget(null);
      setReason("");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  if (!stats || !partners) return <div className="section-dark min-h-screen flex items-center justify-center"><Loader2 size={22} className="animate-spin text-gold" /></div>;

  const STAT_CARDS = [
    ["TOTAL PARTNERS", stats.total, "text-slate-100"], ["PENDING OTP", stats.pending_otp, "text-slate-400"],
    ["PENDING APPROVAL", stats.pending_approval, "text-amber-400"], ["ACTIVE", stats.active, "text-emerald-400"],
    ["REJECTED", stats.rejected, "text-red-400"], ["SUSPENDED", stats.suspended, "text-orange-400"],
  ];

  return (
    <section className="section-dark min-h-screen py-10 px-4 md:px-8" data-testid="admin-partners-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="gold-kicker mb-2 flex items-center gap-2"><Handshake size={13} /> Partner Service Station</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-100">PARTNER APPROVALS</h1>
          </div>
          <Link to="/admin" data-testid="partners-back-btn" className="btn-outline text-xs"><ArrowLeft size={14} /> BACK TO DASHBOARD</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="partner-stats">
          {STAT_CARDS.map(([l, v, c]) => (
            <div key={l} className="card-surface p-4 text-center">
              <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
              <p className={`font-mono text-2xl font-semibold mt-1 ${c}`}>{v}</p>
            </div>
          ))}
        </div>

        <div className="card-surface p-4 mb-6 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="partner-filters">
          <div className="relative col-span-2">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name / Mobile / Email / Partner ID" data-testid="partner-search" className="input-dark !pl-8 !py-2 text-xs w-full" />
          </div>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="partner-status-filter" className="input-dark !py-2 text-xs">
            <option value="" className="bg-navy-900">All statuses</option>
            {Object.keys(STATUS_CLS).map((s) => <option key={s} value={s} className="bg-navy-900">{s}</option>)}
          </select>
          <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="City" data-testid="partner-city-filter" className="input-dark !py-2 text-xs" />
          <input value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} placeholder="Country" data-testid="partner-country-filter" className="input-dark !py-2 text-xs" />
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="partner-date-filter" className="input-dark !py-2 text-xs" />
        </div>

        <div className="card-surface p-5 overflow-x-auto" data-testid="partners-table">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-4">Pending & All Applications ({partners.length})</p>
          {partners.length === 0 ? <p className="text-sm text-slate-500">No partner applications match.</p> : (
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="pb-3 pr-4">Name</th><th className="pb-3 pr-4">Mobile</th><th className="pb-3 pr-4">Email</th><th className="pb-3 pr-4">Company</th><th className="pb-3 pr-4">Location</th><th className="pb-3 pr-4">Registered</th><th className="pb-3 pr-4">OTP</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.partner_id} className="border-b border-slate-800/60" data-testid={`partner-row-${p.partner_id}`}>
                    <td className="py-3 pr-4 font-semibold text-slate-100">{p.full_name}<p className="text-[9px] font-mono text-slate-500">{p.partner_id}</p></td>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-400">{p.mobile}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-400">{p.email}</td>
                    <td className="py-3 pr-4 text-xs text-slate-400">{p.company_name || "—"}</td>
                    <td className="py-3 pr-4 text-xs text-slate-400">{p.city}, {p.country}</td>
                    <td className="py-3 pr-4 font-mono text-[10px] text-slate-500">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="py-3 pr-4 text-xs">{p.otp_verified_at ? <span className="text-emerald-400">✓</span> : <span className="text-slate-600">—</span>}</td>
                    <td className="py-3 pr-4"><span className={`border rounded-sm px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider ${STATUS_CLS[p.status]}`}>{p.status}</span></td>
                    <td className="py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => openView(p)} data-testid={`view-${p.partner_id}`} className="btn-outline !px-2 !py-1 text-[10px]"><Eye size={11} /></button>
                        {p.status === "PENDING_ADMIN_APPROVAL" && (
                          <>
                            <button onClick={() => setConfirmApprove(p)} data-testid={`approve-${p.partner_id}`} className="btn-gold !px-2 !py-1 text-[10px]"><CheckCircle2 size={11} /></button>
                            <button onClick={() => { setRejectTarget(p); setRejectMode("reject"); }} data-testid={`reject-${p.partner_id}`} className="btn-outline !px-2 !py-1 text-[10px] !border-red-500/40 text-red-400"><XCircle size={11} /></button>
                          </>
                        )}
                        {p.status === "ACTIVE" && (
                          <button onClick={() => { setRejectTarget(p); setRejectMode("suspend"); }} data-testid={`suspend-${p.partner_id}`} className="btn-outline !px-2 !py-1 text-[10px] !border-orange-500/40 text-orange-400"><Ban size={11} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {confirmApprove && (
        <div className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setConfirmApprove(null)} data-testid="approve-confirm-modal">
          <div className="card-surface hairline-gold p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <p className="font-display font-bold text-slate-100 mb-2">Approve this partner and activate their account?</p>
            <p className="text-xs text-slate-400 mb-5">{confirmApprove.full_name} ({confirmApprove.email}) — a unique referral link will be generated and an activation email sent.</p>
            <div className="flex gap-3">
              <button onClick={approve} disabled={working} data-testid="approve-confirm-btn" className="btn-gold text-xs flex-1 disabled:opacity-60">{working ? "APPROVING…" : "APPROVE & ACTIVATE"}</button>
              <button onClick={() => setConfirmApprove(null)} data-testid="approve-cancel-btn" className="btn-outline text-xs">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setRejectTarget(null)} data-testid="reason-modal">
          <div className="card-surface p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <p className="font-display font-bold text-slate-100 mb-2">{rejectMode === "reject" ? "Reject application" : "Suspend account"} — {rejectTarget.full_name}</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (required, min 5 characters)" data-testid="reason-input" className="input-dark resize-none text-xs w-full mb-4" />
            <div className="flex gap-3">
              <button onClick={submitReason} disabled={working || reason.trim().length < 5} data-testid="reason-submit-btn" className="btn-gold text-xs flex-1 disabled:opacity-60">{working ? "SAVING…" : rejectMode === "reject" ? "REJECT" : "SUSPEND"}</button>
              <button onClick={() => { setRejectTarget(null); setReason(""); }} data-testid="reason-cancel-btn" className="btn-outline text-xs">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {selected && !confirmApprove && !rejectTarget && (
        <div className="fixed inset-0 z-40 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="partner-view-modal">
          <div className="card-surface p-6 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="font-display font-bold text-slate-100">{selected.full_name}</p>
            <p className="text-[10px] font-mono text-slate-500 mb-4">{selected.partner_id} · {selected.email} · {selected.mobile}</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-5">
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase">Status</dt><dd className="text-slate-200">{selected.status}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase">Location</dt><dd className="text-slate-200">{selected.city}, {selected.country}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase">Approved by</dt><dd className="text-slate-200">{selected.approved_by || "—"}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase">Referral</dt><dd className="text-gold font-mono text-[10px] break-all">{selected.referral_link || "—"}</dd></div>
            </dl>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-2">Audit Trail</p>
            <div className="space-y-1.5">
              {audit.map((a, i) => (
                <p key={i} className="text-[11px] text-slate-400 border-b border-slate-800/60 pb-1.5"><span className="font-mono text-slate-600">{(a.ts || "").slice(5, 16).replace("T", " ")}</span> — {a.detail} <span className="text-slate-600">({a.actor})</span></p>
              ))}
              {!audit.length && <p className="text-xs text-slate-500">No audit events.</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
